import React, { useEffect, useState } from "react";
import { Accordion, OverlayTrigger, Tooltip } from "react-bootstrap";
import PropTypes from "prop-types";

import ActionButton from "../formInputs/buttons/ActionButton";
import PermissionButtonGroup from "../formInputs/PermissionButtonGroup";
import PermissionDropdown from "../formInputs/PermissionDropdown";

import "../../styles/GroupShareTable.scss";

/**
 * @component GroupShareTable
 * @description Displays a list of shared grading profiles in either a compact accordion layout or table layout,
 * depending on screen size. Allows adjusting permissions, saving changes, and removing shares.
 *
 * @param {Object} props
 * @param {Object} accordionItemRefs - Ref object to store references to accordion items.
 * @param {Array<Object>} existingShares - List of current shares with grading criteria and metadata.
 * @param {Function} handleDeleteShare - Callback to remove a share entry.
 * @param {Object} grade - Grade object that includes subject mappings.
 * @param {string|number} openAccordionKey - Key of currently open accordion item.
 * @param {Function} onPermissionChange - Callback when a permission level changes.
 * @param {Function} onSave - Callback when shares are saved.
 * @param {Object} selectedEpr - Currently selected Elementary Progress Report data.
 * @param {Function} setOpenAccordionKey - Sets which accordion item is expanded.
 */

const GroupShareTable = ({
    accordionItemRefs,
    existingShares,
    handleDeleteShare,
    grade,
    openAccordionKey,
    onPermissionChange,
    onSave,
    selectedEpr,
    setOpenAccordionKey
}) => {
    const [isCompact, setIsCompact] = useState(false);
    const [editedShares, setEditedShares] = useState([]);

    const handlePermissionChange = (shareKey, criteriaKey, newValue) => {
        onPermissionChange(shareKey, criteriaKey, newValue);

        // Track share as edited if not already
        if (!editedShares.includes(shareKey)) {
            setEditedShares((prev) => [...prev, shareKey]);
        }
    };

    const resolveCriteriaId = (subject, gradingCriteriaList) => {
        const criteria = gradingCriteriaList.find((item) => item.parentTemplate === subject.templateId);
        return criteria?.key;
    };

    const isSaveDisabled = (share) => share.gradingCriteriaShares.every((s) => s.permission === "NONE");

    const sortedShares = [...existingShares].sort((a, b) => {
        if (a.isNew && !b.isNew) return -1;
        if (!a.isNew && b.isNew) return 1;

        const keyA = parseInt(a.key.toString().replace("temp-", ""), 10);
        const keyB = parseInt(b.key.toString().replace("temp-", ""), 10);
        return keyB - keyA;
    });

    /**
     * Checks screen size to switch to Accordions when screen width is <=1300px
     */
    useEffect(() => {
        const handleResize = () => {
            setIsCompact(window.innerWidth <= 1300);
        };

        handleResize(); // initial check
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return isCompact ? (
        <div className="group-share-accordion">
            <Accordion activeKey={openAccordionKey} onSelect={(key) => setOpenAccordionKey(key)}>
                {sortedShares.map((share) => {
                    const viewCount = share.gradingCriteriaShares.filter((s) => s.permission === "VIEW").length;
                    const editCount = share.gradingCriteriaShares.filter((s) => s.permission === "EDIT").length;
                    const total = viewCount + editCount;
                    const isNew = share.isNew;

                    return (
                        <Accordion.Item
                            eventKey={share.key.toString()}
                            key={share.key}
                            ref={(el) => {
                                if (el) accordionItemRefs.current[share.key.toString()] = el;
                            }}
                        >
                            <Accordion.Header>
                                {share.displayName}
                                <span style={{ marginLeft: 12 }}>
                                    — {total} {total > 1 ? "subjects" : "subject"} shared
                                </span>
                            </Accordion.Header>

                            <Accordion.Body>
                                <div className="epr-subjects">
                                    {grade?.subjectToStandards.map((subject) => {
                                        const criteriaKey = resolveCriteriaId(subject, selectedEpr.gradingCriteriaList);

                                        if (!criteriaKey) return null;
                                        const gradingCriteriaId = criteriaKey;

                                        const existingShare = share.gradingCriteriaShares.find(
                                            (s) => parseInt(s.gradingCriteriaId, 10) === parseInt(gradingCriteriaId, 10)
                                        );

                                        const existingPermission = existingShare?.permission?.toLowerCase() ?? "none";

                                        return (
                                            <div key={subject.templateId} className="subject-card">
                                                <PermissionButtonGroup
                                                    criteriaKey={gradingCriteriaId.toString()}
                                                    subject={subject.subjectName}
                                                    initialValue={existingPermission}
                                                    onChange={(criteriaKey, newValue) =>
                                                        handlePermissionChange(share.key, criteriaKey, newValue)
                                                    }
                                                />
                                            </div>
                                        );
                                    })}

                                    <div className="share-save-btn">
                                        {isSaveDisabled(share) ? (
                                            <OverlayTrigger
                                                placement="top"
                                                delay={300}
                                                overlay={<Tooltip>Select at least one subject to enable Save</Tooltip>}
                                            >
                                                <span className="d-inline-block">
                                                    <ActionButton
                                                        className="action-button-reg"
                                                        label={isNew ? "Save Share" : "Save Changes"}
                                                        onClick={() => onSave(share)}
                                                        disable={isSaveDisabled(share)}
                                                    />
                                                </span>
                                            </OverlayTrigger>
                                        ) : (
                                            <ActionButton
                                                className="action-button-reg"
                                                label={isNew ? "Save Share" : "Save Changes"}
                                                onClick={() => onSave(share)}
                                            />
                                        )}
                                        <ActionButton
                                            className="action-button-delete"
                                            label="Remove"
                                            onClick={() => handleDeleteShare(share)}
                                        />
                                    </div>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    );
                })}
            </Accordion>
            <div
                style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "1rem",
                    marginLeft: "10px"
                }}
            >
                <ActionButton
                    className="action-button-reg"
                    label="Save"
                    onClick={() => {
                        const toSave = existingShares.filter((s) => editedShares.includes(s.key));
                        onSave(toSave);
                        setEditedShares([]);
                    }}
                    disable={editedShares.length === 0}
                />
            </div>
        </div>
    ) : (
        <>
            <table className="group-share-table">
                <thead>
                    <tr>
                        <th>Teacher</th>
                        {grade?.subjectToStandards.map((subject) => (
                            <th key={subject.templateId}>{subject.subjectName}</th>
                        ))}
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedShares.map((share) => {
                        return (
                            <tr key={share.key}>
                                <td>{share.displayName}</td>
                                {grade?.subjectToStandards.map((subject) => {
                                    const criteriaKey = resolveCriteriaId(subject, selectedEpr.gradingCriteriaList);

                                    const gradingCriteriaId = criteriaKey;
                                    if (!gradingCriteriaId) return <td key={subject.templateId}>—</td>;

                                    const existingShare = share.gradingCriteriaShares.find(
                                        (s) => parseInt(s.gradingCriteriaId, 10) === parseInt(gradingCriteriaId, 10)
                                    );
                                    const currentPermission = existingShare?.permission?.toLowerCase() || "none";

                                    return (
                                        <td key={subject.templateId}>
                                            <PermissionDropdown
                                                criteriaKey={gradingCriteriaId}
                                                shareKey={share.key}
                                                current={currentPermission}
                                                onChange={handlePermissionChange}
                                            />
                                        </td>
                                    );
                                })}
                                <td>
                                    <button
                                        style={{ background: "transparent", border: "none" }}
                                        onClick={() => handleDeleteShare(share)}
                                    >
                                        <i className="bi bi-trash h6" />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginLeft: "10px" }}>
                <ActionButton
                    className="action-button-reg"
                    label="Save"
                    onClick={() => {
                        const toSave = existingShares.filter((s) => editedShares.includes(s.key));
                        onSave(toSave);
                        setEditedShares([]);
                    }}
                    disable={editedShares.length === 0}
                />
            </div>
        </>
    );
};

GroupShareTable.propTypes = {
    accordionItemRefs: PropTypes.shape({
        current: PropTypes.objectOf(PropTypes.any)
    }).isRequired,
    existingShares: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            guid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            displayName: PropTypes.string,
            gradingCriteriaShares: PropTypes.arrayOf(
                PropTypes.shape({
                    gradingCriteriaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    permission: PropTypes.oneOf(["NONE", "VIEW", "EDIT"]).isRequired
                })
            ),
            isNew: PropTypes.bool
        })
    ).isRequired,
    handleDeleteShare: PropTypes.func.isRequired,
    grade: PropTypes.shape({
        subjectToStandards: PropTypes.arrayOf(
            PropTypes.shape({
                templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                subjectName: PropTypes.string.isRequired
            })
        )
    }),
    openAccordionKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onPermissionChange: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    selectedEpr: PropTypes.shape({
        gradingCriteriaList: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                parentTemplate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
            })
        )
    }).isRequired,
    setOpenAccordionKey: PropTypes.func.isRequired
};

GroupShareTable.defaultProps = {
    grade: null,
    openAccordionKey: null
};

export default GroupShareTable;
